<template lang="pug">
#move-room-schedule-register
  .mb-3
  .box-body.register-body
    .note
      span 転床もしくは仮押さえの予定を登録できます。登録すると部屋スケジュールに反映されます。
      br
      span 転床の場合、転床する利用者、転床先の部屋、開始日時と終了日時は転床期間を選択してください。
    FormInputRadio(
      v-model="type"
      keyName="種別" name="type" :options="typeOptions"
      :validationRules="{ required: true }" required)
    FormInputSelect(
      v-model="roomId"
      key-name="部屋" name="room-id"
      :options="roomOptions" :styles="{ 'max-width': '540px' }"
      :validation-rules="{ required: true }" required)
    FormInputSelect(
      v-if="type == 0"
      v-model="residentGroupId"
      key-name="利用者グループ" name="resident-group"
      :options="residentGroupOptions" :styles="{ 'max-width': '540px' }")
    FormInputSelect(
      v-if="type == 0"
      v-model="residentId"
      key-name="利用者" name="resident"
      :options="residentOptions" :styles="{ 'max-width': '540px' }"
      :validation-rules="{ required: true }" required)
    FormDatetimePicker(
      v-model="startDatetime" :date.sync="startDatetimeDate" :time.sync="startDatetimeTime"
      placeholder="開始日時" key-name="開始日時" name="start-datetime"
      :validation-rules="{ required: true }" required)
    FormDatetimePicker(
      v-model="endDatetime" :date.sync="endDatetimeDate" :time.sync="endDatetimeTime"
      placeholder="終了日時" key-name="終了日時" name="end-datetime"
      :validation-rules="{ required: true }" required)
  .box-footer.register-footer
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0
      button.btn.register-btn(@click="onRegister" type="button") 登録
      button.btn.cancel-btn(@click="onCancel" type="button") キャンセル

  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { MoveRoomSchedulesType, MoveRoomSchedulesPostModel, MoveRoomSchedulesPostUrlParam } from "@/commons/services/MoveRoomSchedules";
import RoomsService, { RoomGetModel, RoomType, UseType } from "@/commons/services/Rooms";
import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import SystemError from "@/commons/models/SystemError";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputRadio from "@/pc/components/forms/FormInputRadio.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";

/**
 * ### 転床／仮押さえ情報登録タブコンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    ValidationAlert,
    FormInput,
    FormInputRadio,
    FormInputSelect,
    FormDatetimePicker
  },
  $_veeValidate: { validator: "new" }
})
export default class MoveRoomScheduleRegisterTab extends Vue {
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 種別 */
  type: MoveRoomSchedulesType | null = null;
  /** 種別選択項目 */
  typeOptions: Array<{ value: number | string; label: string }> = [];
  /** 部屋ID */
  roomId: number | null = null;
  /** 部屋選択項目 */
  roomOptions: Array<{ value: number | string; label: string }> = [];
  /** 利用者名MAP。key：利用者ID、value：利用者名 */
  roomNameMap: Map<number, string> = new Map<number, string>();

  /** 利用者グループID */
  residentGroupId: number = 0;
  /** 利用者グループセレクトボックス用項目 */
  residentGroupOptions: Array<{ value: number | string; label: string }> = [];
  /** セレクトボックス用項目で使用する利用者 */
  residents: Array<ResidentGetModel> = [];
  /** 利用者ID */
  residentId: number | null = null;
  /** 利用者セレクトボックス用項目 */
  residentOptions: Array<{ value: number | string; label: string }> = [];
  /** 利用者名MAP。key：利用者ID、value：利用者名 */
  residenNameMap: Map<number, string> = new Map<number, string>();

  /** 開始日時 */
  startDatetime: number | null = null;
  /** 開始日時(日) */
  startDatetimeDate: string = "";
  /** 開始日時(時) */
  startDatetimeTime: string = "";
  /** 終了日時 */
  endDatetime: number | null = null;
  /** 終了日時(日) */
  endDatetimeDate: string = "";
  /** 終了日時(時) */
  endDatetimeTime: string = "";

  /**
   * ### 利用者グループIDの値更新を監視する
   *
   * - 利用者IDをリセットする
   * - 利用者のセレクトボックス選択項目を更新する
   */
  @Watch("residentGroupId")
  public async changeResidentGroupId(newValue: number): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const residentOptions: Array<{ value: number; label: string }> = this.createResidentOptions(this.residents, newValue);
    this.residentId = null;
    this.residentOptions = residentOptions;

    this.isBusy = false;

    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 種別の選択項目を作成する
   * - 部屋を取得し、部屋の選択項目を作成する
   * - 登録時に使用する部屋名MAPを作成する
   * - 利用者の選択項目を作成する
   * - 登録時に使用する利用者名MAPを作成する
   * - 利用者グループを取得し、利用者グループの選択項目を作成する
   * - 開始日時、終了日時を今日で初期化する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const typeOptions: Array<{ value: number | string; label: string }> = this.createTypeOptions();
    this.typeOptions = typeOptions;

    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomOptions: Array<{ value: number | string; label: string }> = this.createRoomOptions(rooms);
    this.roomOptions = roomOptions;

    const roomNameMap: Map<number, string> = this.createRoomNameMap(rooms);
    this.roomNameMap = roomNameMap;

    const residents: Array<ResidentGetModel> = await this.getResidents();
    const residentOptions: Array<{ value: number; label: string }> = this.createResidentOptions(residents, this.residentGroupId);
    this.residents = residents;
    this.residentOptions = residentOptions;

    const residenNameMap: Map<number, string> = this.createResidenNameMap(residents);
    this.residenNameMap = residenNameMap;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const residentGroupOptions: Array<{ value: number; label: string }> = this.createResidentGroupOptions(residentGroups, residents);
    this.residentGroupId = 0;
    this.residentGroupOptions = residentGroupOptions;

    const startDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const startDatetimeTime: string = "10:00:00";
    this.startDatetimeDate = startDatetimeDate;
    this.startDatetimeTime = startDatetimeTime;
    const endDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const endDatetimeTime: string = "10:00:00";
    this.endDatetimeDate = endDatetimeDate;
    this.endDatetimeTime = endDatetimeTime;

    this.isBusy = false;

    return;
  }

  /**
   * ### 種別セレクトボックス用項目を作成する
   */
  public createTypeOptions(): Array<{ value: number; label: string }> {
    let typeOptions: Array<{ value: number; label: string }> = [];

    typeOptions.push({ value: MoveRoomSchedulesType.CHANGE_ROOM, label: "転床" });
    typeOptions.push({ value: MoveRoomSchedulesType.TEMPORARY, label: "仮押さえ" });

    return typeOptions;
  }

  /**
   * ### 部屋を取得する
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  /**
   * ### 部屋セレクトボックス用項目を作成する
   */
  public createRoomOptions(rooms: Array<RoomGetModel>): Array<{ value: number; label: string }> {
    let roomOptions: Array<{ value: number; label: string }> = [];

    for (const room of rooms) {
      const useType: string = this.convertUseTypeName(room.useType);
      const roomType: string = this.convertRoomTypeName(room.roomType);

      if (room.roomType == RoomType.MULTI_MEN || room.roomType == RoomType.MULTI_WOMEN) {
        roomOptions.push({
          value: room.id,
          label: `${room.name}（${useType}・${roomType}・${room.capacity}人）`
        });
      } else {
        roomOptions.push({
          value: room.id,
          label: `${room.name}（${useType}・${roomType}）`
        });
      }
    }

    return roomOptions;
  }

  /**
   * ### 用途名を返す
   */
  public convertUseTypeName(useType: UseType): string {
    if (useType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (useType == UseType.NORMAL_STAY) {
      return "入居用部屋";
    } else if (useType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  /**
   * ### 部屋種別名を返す
   */
  public convertRoomTypeName(roomType: RoomType): string {
    if (roomType == RoomType.UNMANAGED) {
      return "空床管理対象外";
    } else if (roomType == RoomType.SINGLE) {
      return "個室";
    } else if (roomType == RoomType.DOUBLE) {
      return "２人部屋（夫婦部屋）";
    } else if (roomType == RoomType.MULTI_MEN) {
      return "多床室（男性）";
    } else if (roomType == RoomType.MULTI_WOMEN) {
      return "多床室（女性）";
    } else {
      return "";
    }
  }

  /**
   * ### 部屋名MAPを作成する
   *
   * - 仮押さえ／転床登録時に使用するためにMAPを作成して保持する
   * - key：部屋ID、value：部屋名のMAPを作成する
   */
  public createRoomNameMap(rooms: Array<RoomGetModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();
    for (const room of rooms) {
      map.set(room.id, room.name);
    }

    return map;
  }

  /**
   * ### 利用者取得
   */
  public async getResidents(): Promise<Array<ResidentGetModel>> {
    let response: Array<ResidentGetModel> = [];

    try {
      response = await this.residentsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.residentsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.residentsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 利用者セレクトボックス用項目を作成する
   *
   * - 渡されたグループIDを持つ利用者のみを項目にする
   * - 渡されたグループIDが0(全て)の場合は全利用者を項目にする
   * - 選択肢の表示項目名は 利用者名（利用者部屋名）
   */
  public createResidentOptions(residents: Array<ResidentGetModel>, groupId: number): Array<{ value: number; label: string }> {
    let options: Array<{ value: number; label: string }> = [];

    for (const resident of residents) {
      if (resident.groupId == groupId || groupId == 0) {
        options.push({
          value: resident.id,
          label: `${resident.name}（${resident.room}）`
        });
      }
    }

    return options;
  }

  /**
   * ### 利用者名MAPを作成する
   *
   * - 仮押さえ／転床登録時に使用するためにMAPを作成して保持する
   * - key：利用者ID、value：利用者名のMAPを作成する
   */
  public createResidenNameMap(residents: Array<ResidentGetModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();
    for (const resident of residents) {
      map.set(resident.id, resident.name);
    }

    return map;
  }

  /**
   * ### 利用者グループ取得
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループセレクトボックス用項目を作成する
   *
   * - 利用者グループに「全て」を追加する
   * - 全て以外は渡された利用者グループで項目を作成する
   *   - ユーザーの権限を見て、閲覧できない権限の利用者グループは選択肢にしない
   *   - 利用者が一人もいない利用者グループは選択肢にしない
   */
  public createResidentGroupOptions(residentGroups: Array<ResidentGroupsGetModel>, residents: Array<ResidentGetModel>): Array<{ value: number; label: string }> {
    const options: Array<{ value: number; label: string }> = [];

    options.push({
      value: 0,
      label: `全て`
    });

    const canUseResidentGroupMap: Map<number, number> = new Map<number, number>();
    for (const resident of residents) {
      canUseResidentGroupMap.set(resident.groupId, resident.groupId);
    }
    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (canUseResidentGroupMap.has(group.id) && allowedGroupsMap.has(group.id)) {
        options.push({
          value: group.id,
          label: `${group.name}`
        });
      }
    }

    return options;
  }

  /**
   * ### 登録ボタン押下イベント
   *
   * - バリデーションを実施する
   * - バリデーションエラーならライブラリ経由でエラーメッセージを表示する
   * - エラーでなければ仮押さえ／転床登録用の情報を作成して親コンポーネントに通知する
   */
  public async onRegister(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    const startEndValidationResult: boolean = this.startEndValidate(this.startDatetime, this.endDatetime);
    const endValidationResult: boolean = this.endValidate(this.endDatetime);
    if (validationResult == false || startEndValidationResult == false || endValidationResult == false) {
      this.showValidationAlert = true;
      return;
    } else {
      this.showValidationAlert = false;
    }

    const urlPatam: MoveRoomSchedulesPostUrlParam = {
      startDatetime: this.startDatetime as number,
      roomId: this.roomId as number,
      residentId: this.residentId ? this.residentId : 0
    };
    const requestBody: MoveRoomSchedulesPostModel = this.createRequestBody();

    this.$emit("onRegister", { urlPatam, requestBody });

    return;
  }

  /**
   * ### 開始日時・終了日時のバリデーション
   *
   * - 開始日時が終了日時よりも未来ならエラーとして扱う
   * - エラーではない(true)、エラー(false)
   */
  public startEndValidate(startDatetime: number | null, endDatetime: number | null): boolean {
    if (startDatetime == null || endDatetime == null) {
      return true;
    } else {
      if (endDatetime < startDatetime) {
        this.$validator.errors.add({ field: "start-datetime", msg: "開始日時が終了日時より未来です" });
        return false;
      } else {
        return true;
      }
    }
  }

  /**
   * ### 終了日時のバリデーション
   *
   * - 終了日時が当月1日より過去の場合はエラーとして扱う
   * - エラーではない(true)、エラー(false)
   */
  public endValidate(endDatetime: number | null): boolean {
    if (endDatetime == null) {
      return true;
    } else {
      const firstDate: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymm01000000000");
      if (endDatetime < firstDate) {
        this.$validator.errors.add({ field: "end-datetime", msg: "終了日時が当月1日より過去です" });
        return false;
      } else {
        return true;
      }
    }
  }

  /**
   * ### 仮押さえ／転床情報登録パラメータ作成
   *
   * - 種別が「仮押さえ」の場合は利用者IDはnullなので、利用者名は空文字で送信する
   */
  public createRequestBody(): MoveRoomSchedulesPostModel {
    const residentId: number = this.residentId as number;
    const residentName: string | undefined = this.residenNameMap.get(residentId);
    const roomId: number = this.roomId as number;
    const roomName: string | undefined = this.roomNameMap.get(roomId);

    const requestBody: MoveRoomSchedulesPostModel = {
      endDatetime: this.endDatetime as number,
      roomName: roomName as string,
      residentName: residentName ? residentName : "",
      type: this.type as MoveRoomSchedulesType
    };

    return requestBody;
  }

  /**
   * ### キャンセルボタン押下イベント
   *
   * - キャンセルボタン押下を親コンポーネントに通知する
   */
  public onCancel(): void {
    this.$emit("onCancel");
    return;
  }
}
</script>

<style lang="scss" scoped>
#move-room-schedule-register {
  .note {
    margin: 0 0 20px 20px;
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
